import React, { useEffect } from "react";
import App from "./App";
import AppContextProvider from "./store/genesys.context";
import CoBrowseContextProvider from "./store/coBrowse.context";
import { injectFonts } from "./utils/injectFonts";
import ChatControlsBridge from "./lib/chatControlsBridge";
import HostBridge from "./iframe/hostBridge";
import { subscribeToHostScroll } from "./iframe/hostProtocol";
import TeaserRevealProvider from "./store/teaserReveal.context";
import { subscribeToWindowScroll } from "./utils/teaserReveal";
import { validateChatbotConfig } from "./utils/validateChatbotConfig";
import { reportAppError, reportAppWarning } from "./utils/reportAppError";
import type { ChatbotConfig, ChatControls } from "./lib/ChatbotConfig";

interface GenesysChatbotProps {
  config: ChatbotConfig;
  /**
   * Internal — set only by the standalone iframe deployment. Wires up the
   * postMessage bridge that keeps the host page's iframe element the size of
   * the widget. Package consumers mount the widget into their own DOM, where
   * the widget sizes itself and there is no host to talk to.
   */
  hostBridge?: boolean;
  /**
   * Called with the imperative chat controls once the widget has mounted, and
   * with `null` when it unmounts. React hosts can stash them in a ref to open
   * the chat from their own UI; `mountChatbot` uses this internally.
   */
  onControlsReady?: (controls: ChatControls | null) => void;
}

const GenesysChatbot = ({
  config: hostConfig,
  onControlsReady,
  hostBridge,
}: GenesysChatbotProps): React.JSX.Element => {
  // Every embedding — React host, mountChatbot, standalone app — enters here, so
  // this is the one place that has to cope with a config TypeScript never got to
  // check. Everything below the boundary can rely on brand and environment.
  const { config, issues } = React.useMemo(
    () => validateChatbotConfig(hostConfig),
    [hostConfig],
  );

  // Hosts pass their config as an object literal, so `issues` is a fresh array
  // on every render — keying the effect on its contents keeps a misconfigured
  // embedding from logging on every re-render.
  const issueKey = issues.map((issue) => issue.message).join("|");

  useEffect(() => {
    issues.forEach(({ level, scope, message, metadata }) => {
      const report = level === "error" ? reportAppError : reportAppWarning;

      report({ scope, error: new Error(message), metadata });
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issueKey]);

  // Inject the custom @font-face rules so the library build ships its own fonts
  // (see injectFonts). Idempotent, so safe across remounts / StrictMode.
  useEffect(() => {
    injectFonts();
  }, []);

  return (
    <AppContextProvider config={config}>
      <TeaserRevealProvider
        subscribe={hostBridge ? subscribeToHostScroll : subscribeToWindowScroll}
      >
        <CoBrowseContextProvider>
          {onControlsReady && (
            <ChatControlsBridge onControlsReady={onControlsReady} />
          )}
          {hostBridge && <HostBridge />}
          <App config={config} />
        </CoBrowseContextProvider>
      </TeaserRevealProvider>
    </AppContextProvider>
  );
};

export default GenesysChatbot;
